import { useEffect, useMemo, useRef } from 'react';
import { applyAutoAnswers, applyLegacyAutoAnswers, qcPrefillGroups } from '../../pages/qcQuestions';

/**
 * Prefill from the covered engagements for the surfaces that START a QC6 /
 * QC7 form (docs/modules/workflow-qc.md):
 *
 *   WorkflowQcNew   New QC6 / QC7 Forms request (and its draft)
 *   QMQcRecordNew   Management → New QC6 / QC7 Record — the full form and
 *                   the legacy-import form alike (`legacy`: PIE and the fees
 *                   only, the questions that form asks)
 *
 * The picker feeds carry each engagement's PIE answer and Estimated Fees,
 * plus `screeningAnswers` (the TNE / TNA pair the
 * creating Registration answered when it asked for QM screening). Each group
 * is written in only while its answers are still blank, so a draft or an
 * edited answer is never overwritten; everything stays editable. Several
 * covered engagements: a group that differs per engagement is written per
 * engagement (qcQuestions.qcPrefillGroups).
 *
 * `setAnswers` must accept an updater function (prev => next).
 */
const blank = (v) => v === undefined || v === null || v === ''
  || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

export const useQcPrefill = ({ engagements, qcType, answers, setAnswers, enabled = true, legacy = false }) => {
  const groups = useMemo(
    () => (enabled ? qcPrefillGroups(engagements, qcType, { legacy }) : []),
    [engagements, qcType, enabled, legacy]
  );
  // A group is written while its answers are blank and retired the first time
  // it is seen answered — by this prefill, a draft or the user. The write is
  // retried on every answers change until then: QcForm's own auto-answer pass
  // (a plain setState from its snapshot of the answers) can land after this
  // one in the same commit and put the blank answers back — picking the
  // engagement first and the QC type second does exactly that. Once retired a
  // cleared answer stays cleared.
  const seen = useRef({ groups: null, keys: new Set() });
  useEffect(() => {
    if (groups.length === 0) return;
    if (seen.current.groups !== groups) seen.current = { groups, keys: new Set() };
    const cur = answers || {};
    const todo = [];
    groups.forEach((g) => {
      if (seen.current.keys.has(g.key)) return;
      if (g.ids.every((id) => blank(cur[id]))) todo.push(g);
      else seen.current.keys.add(g.key);
    });
    if (todo.length === 0) return;
    setAnswers((prev) => {
      let next = prev || {};
      let changed = false;
      todo.forEach((g) => {
        if (g.ids.some((id) => !blank(next[id]))) return;
        next = { ...next, ...g.answers };
        changed = true;
      });
      if (!changed) return prev || {};
      return legacy ? applyLegacyAutoAnswers(next, qcType) : applyAutoAnswers(next, qcType);
    });
  }, [groups, answers, qcType, legacy]); // eslint-disable-line react-hooks/exhaustive-deps
};
